import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import DateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import MessageFileInfoSchema from "messaging/attachments/_schemas/message-file-info-schema";
import MessageFileInfo from "messaging/attachments/file-info/message-file-info";
import Forum from "messaging/forums/forum";
import ForumSchema from "messaging/forums/_schemas/forum-schema";
import ReadReceiptSchema from "messaging/read-receipts/_schemas/read-receipt-schema";
import ReadReceipt from "messaging/read-receipts/read-receipt";
import { z } from "zod";
import AHDate from "_shared/dates/date";

export type MessageJSON = z.infer<typeof MessageSchema.MessageJSONZ>;
export type MessageParams = z.infer<typeof MessageSchema.MessageZ>;

export default class MessageSchema {
  public static readonly MessageJSONZ = z.object({
    id: z.uuid(),
    senderId: z.uuid().nullish(),
    forum: ForumSchema.ForumJSONZ,
    content: z.string().nullish(),
    subject: z.string().nullish(),
    created: z.iso.datetime({ offset: true }),
    deleted: z.iso.datetime({ offset: true }).nullish(),
    updated: z.iso.datetime({ offset: true }).nullish(),
    published: z.iso.datetime({ offset: true }).nullish(),
    attachments: z.array(MessageFileInfoSchema.MessageFileInfoJSONZ).nullish(),
    issues: z.array(z.string()).nullish(),
    readReceipts: z.array(ReadReceiptSchema.ReadReceiptJSONZ).nullish(),
    isAutoGenerated: z.boolean().nullish(),
  });

  public static readonly MessageZ = z.object({
    id: z.instanceof(Guid).optional(),
    senderId: z.instanceof(Guid).optional().nullable(),
    forum: z.instanceof(Forum),
    content: z.string(),
    subject: z.string().optional(),
    attachments: z.array(z.instanceof(MessageFileInfo)).optional(),
    readReceipts: z.array(z.instanceof(ReadReceipt)).optional(),
    issues: z.array(z.string()).optional(),
    isAutoGenerated: z.boolean().optional(),
    created: DateSchema.type.optional(),
    deleted: DateSchema.type.optional(),
    updated: DateSchema.type.optional(),
    published: DateSchema.type.optional(),
  });

  public static validate(values: unknown): MessageParams {
    return parseOrThrow(MessageSchema.MessageZ, values, {
      logError: true,
      schemaName: "MessageSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): MessageJSON {
    return parseOrThrow(MessageSchema.MessageJSONZ, json, {
      logError: true,
      schemaName: "MessageSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is MessageParams {
    try {
      MessageSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is MessageJSON {
    try {
      MessageSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: unknown): MessageParams {
    const parsed = parseOrThrow(MessageSchema.MessageJSONZ, json, {
      logError: true,
      schemaName: "MessageSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return MessageSchema.codec.decode(parsed);
  }

  public static serialize(params: MessageParams): MessageJSON {
    if (isNullOrUndefined(params)) return params;
    const parsed = MessageSchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed;
    return MessageSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(MessageSchema.MessageJSONZ, MessageSchema.MessageZ, {
    decode: (data) => {
      if (!data.content) throw new Error("Message.Deserialize(): Content is required");
      return {
        id: Guid.fromJSON(data.id) ?? undefined,
        senderId: Guid.fromJSON(data.senderId) ?? undefined,
        forum: Forum.fromJSON(data.forum),
        content: data.content,
        subject: data.subject ?? undefined,
        created: AHDate.fromJSON(data.created) ?? undefined,
        deleted: AHDate.fromJSON(data.deleted) ?? undefined,
        updated: AHDate.fromJSON(data.updated) ?? undefined,
        published: AHDate.fromJSON(data.published) ?? undefined,
        attachments: data.attachments?.map((attachment) => MessageFileInfo.fromJSON(attachment)) ?? [],
        issues: data.issues ?? [],
        readReceipts:
          data.readReceipts
            ?.map((receipt) => ReadReceipt.fromJSON(receipt))
            .filter((receipt): receipt is ReadReceipt => receipt !== null && receipt !== undefined) ?? [],
        isAutoGenerated: data.isAutoGenerated ?? false,
      };
    },
    encode: (message) => {
      if (!message.id) throw new Error("Message.Serialize(): Id is required");
      if (!message.created) throw new Error("Message.Serialize(): Created is required");
      return {
        id: message.id.toJSON(),
        senderId: message.senderId?.toJSON(),
        forum: message.forum.toJSON(),
        content: message.content,
        subject: message.subject,
        created: message.created.toJSON(),
        deleted: message.deleted?.toJSON(),
        updated: message.updated?.toJSON(),
        published: message.published?.toJSON(),
        attachments: message.attachments?.map((attachment) => attachment.toJSON()) ?? [],
        issues: message.issues ?? [],
        readReceipts: message.readReceipts?.map((receipt) => receipt.toJSON()) ?? [],
        isAutoGenerated: message.isAutoGenerated ?? false,
      };
    },
  });
}
